import React, { useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { Modal, ModalContent, ModalBody, ModalHeader, ModalFooter, useDisclosure } from "@heroui/react";
import useStore from "../store/useStore";
import { getReceivedSolicitations, getSentSolicitations, setNotificationAsAttended } from "../services/api";
import { ISolicitation } from "../lib/types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { CameraFullscreen } from "../components/CameraFullscreen";
import { BiSolidShare } from "react-icons/bi";
import { getLogoBase64 } from "../utils/themeManager";
import { CgAttachment } from "react-icons/cg";
import { sanitizeHtml } from "../lib/sanitize";

function TabsHeader({
  selected,
  onSelect,
}: {
  selected: string;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="flex justify-center w-full py-2">
      <div className="flex gap-2 bg-white rounded-full p-1 border border-default-200 shadow-sm">
        <button
          className={`px-5 py-1.5 text-sm font-medium rounded-full transition-all relative flex items-center justify-center
            ${selected === "inbox"
              ? "bg-primary text-secondary border-2 border-primary-500 shadow-sm z-10 cursor-default"
              : "bg-white text-primary/60 border border-primary-200"
            }
          `}
          style={{ minWidth: 80 }}
          onClick={() => onSelect("inbox")}
        >
          Recebidos
        </button>
        <button
          className={`px-5 py-1.5 text-sm font-medium rounded-full transition-all relative flex items-center justify-center
            ${selected === "sent"
              ? "bg-primary text-secondary border-2 border-primary-500 shadow-sm z-10 cursor-default"
              : "bg-white text-primary/60 border border-primary-200"
            }
          `}
          style={{ minWidth: 80 }}
          onClick={() => onSelect("sent")}
        >
          Enviadas
        </button>
      </div>
    </div>
  );
}

interface FileState {
  name: string;
  originalName: string;
  base64: string;
  type: string;
  editing: boolean;
  tempName?: string;
}

const isWebView = () => {
  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;
  // iOS WebView
  const isIOSWebView =
    /\b(iPhone|iPod|iPad)\b/.test(userAgent) && !/Safari/.test(userAgent);

  // Android WebView
  const isAndroidWebView = /\bwv\b/.test(userAgent) || /; wv\)/.test(userAgent);

  return isIOSWebView || isAndroidWebView;
};

// No WebView Android, o seletor nativo de arquivo (onShowFileChooser) nem sempre abre a câmera
// via input[capture]. Usar o CameraFullscreen (getUserMedia) evita depender dele.
const isAndroidWebViewUA = () => {
  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;
  return /\bwv\b/.test(userAgent) || /; wv\)/.test(userAgent);
};

function BottomNavigation({ selected }: { selected: string }) {
  const history = useHistory();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [cameraOpen, setCameraOpen] = React.useState(false);
  const [files, setFiles] = React.useState<FileState[]>([]);
  const [reloadFiles, setReloadFiles] = React.useState(false);

  useEffect(() => {
    if (files.length > 0) {
      handleMensagem();
      setReloadFiles(false);
    }
  }, [reloadFiles]);

  const handleMensagem = () => {
    history.push("/new-message", {
      selected,
      files: files.map((item) => ({
        ...item,
        uri: `file:///data/user/0/com.ideia.playbpo/cache/WebPicker/${item.name}`,
      })),
    });
  };

  const handleCamera = () => {
    if (isAndroidWebViewUA()) {
      // Android: abre via CameraFullscreen (getUserMedia) em vez do seletor nativo de arquivo
      setCameraOpen(true);
      return;
    }

    if (isWebView()) {
      // Temporariamente adiciona o atributo capture=environment para abrir a câmera
      if (fileInputRef.current) {
        fileInputRef.current.setAttribute("capture", "environment");
        fileInputRef.current.click();
        // Remove o atributo após clicar para não afetar o comportamento da galeria
        setTimeout(() => {
          fileInputRef.current?.removeAttribute("capture");
        }, 500);
      }
    } else {
      setCameraOpen(true);
    }
  };

  const handleArquivo = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          const base64 = dataUrl.split(",")[1]; // remove prefixo data:<type>;base64,
          const extension = file.name.split(".").pop();
          const mimeType =
            file.type ||
            (file.type.startsWith("image/")
              ? `image/${extension}`
              : `video/${extension}`);
          const uniqueName = `${Date.now()}-${Math.random()
            .toString(36)
            .substring(2, 15)}.${extension}`;
          const uri = `file:///data/user/0/com.ideia.playbpo/cache/WebPicker/${uniqueName}`;

          const type = file.type.startsWith("image/") ? "image" : "video";

          setFiles((prev) => [
            ...prev,
            {
              name: uniqueName,
              originalName: file.name,
              uri,
              type,
              mimeType,
              base64,
              editing: false,
            },
          ]);
          setReloadFiles((prev) => !prev);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  return (
    <>
      <CameraFullscreen
        open={cameraOpen}
        onClose={() => {
          setCameraOpen(false);
          // handleMensagem();
        }}
        onCapture={(image: any) => {
          if (image?.uri && image.uri.startsWith("blob:")) {
            // Converte o Blob URI para base64
            fetch(image.uri)
              .then((res) => res.blob())
              .then((blob) => {
                const reader = new FileReader();

                reader.onloadend = () => {
                  let base64 = reader.result as string;

                  // Verifica se o base64 já contém o prefixo "data:image/*;base64,"
                  if (base64.startsWith("data:")) {
                    base64 = base64.split(",")[1]; // Remove o prefixo "data:image/*;base64,"
                  }

                  const extension = image.name.split(".").pop() || "jpg"; // Pega a extensão
                  const mimeType = image.type || `image/${extension}`; // Usa o MIME type original ou gera com base na extensão
                  const uniqueName = `${image?.name ||
                    `${Date.now()}-${Math.random()
                      .toString(36)
                      .substring(2, 15)}.${extension}`
                    }`;
                  const uri = `file:///data/user/0/com.ideia.playbpo/cache/WebPicker/${uniqueName}`;

                  // Agora salvamos o base64 sem o prefixo
                  setFiles((prev) => [
                    ...prev,
                    {
                      name: uniqueName, // Nome único gerado
                      originalName: image.name,
                      uri, // URI simulada
                      type: mimeType,
                      base64, // Base64 sem o prefixo "data:image/*;base64,"
                      editing: false,
                    },
                  ]);
                };

                reader.readAsDataURL(blob); // Converte o Blob em base64
              })
              .catch((error) => {
                console.error("Erro ao buscar o Blob URI:", error);
              });
          } else {
            // Caso seja outro tipo de URI (base64 já incluído, por exemplo)
            setFiles((prev) => [
              ...prev,
              {
                name: image.name,
                originalName: image.name,
                uri: image.uri,
                type: image.type,
                base64: image?.base64, // Base64 já incluso no caso do arquivo ser base64
                editing: false,
              },
            ]);
          }
          setTimeout(() => {
            setReloadFiles((prev) => !prev);
          }, 500);
        }}
      />
      <nav
        className="fixed bottom-0 left-0 right-0 bg-primary-500 px-4 border-t border-primary-600 z-20 pb-safe-6"
        style={{ border: "0px" }}
      >
        <div className="flex justify-around items-center h-16 max-w-md mx-auto relative">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            variant="light"
            className="flex flex-col items-center text-secondary justify-center h-16 gap-1"
            onPress={handleCamera}
          >
            <Icon icon="lucide:camera" className="text-3xl text-secondary" />
            <span className="text-[0.80rem] leading-tight text-secondary">
              Camera
            </span>
          </Button>
          <Button
            color="primary"
            className="flex flex-col items-center w-16 h-16 rounded-full shadow-lg border-4 border-white -mt-6 z-10 justify-center gap-0"
            onPress={handleMensagem}
          >
            <Icon icon="lucide:plus" className="text-3xl text-secondary" />
            <span className="text-[0.80rem] leading-tight text-secondary mb-1">
              Novo
            </span>
          </Button>
          <Button
            variant="light"
            className="flex flex-col items-center text-secondary justify-center h-16 gap-1"
            onPress={handleArquivo}
          >
            <Icon icon="lucide:file" className="text-3xl text-secondary" />
            <span className="text-[0.80rem] leading-tight text-secondary">
              Arquivo
            </span>
          </Button>
        </div>
      </nav>
    </>
  );
}

export function MessagesPage() {
  const history = useHistory();
  const [selectedMessage, setSelectedMessage] = React.useState<{ id: number; companyId: number } | null>(null);
  const { isOpen: isReadConfirmOpen, onOpen: onReadConfirmOpen, onOpenChange: onReadConfirmChange } = useDisclosure();
  const [selected, setSelected] = React.useState("inbox");
  const { authUser, selectedCompany, setSelectedCompany, logout, isOnline } = useStore();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const location: any = useLocation();

  useEffect(() => {
    if (location?.state?.selected) {
      setSelected(location?.state?.selected);
    }
  }, [location, location.state, location?.state?.selected]);

  const fetchMessages = async ({ pageParam = 0, queryKey }: any) => {
    if (!queryKey[1] || !isOnline) return [];

    const offset = pageParam * 10; // Offset correto

    const data =
      selected === "inbox"
        ? await getReceivedSolicitations(Number(selectedCompany?.id), offset)
        : await getSentSolicitations(Number(selectedCompany?.id), offset);

    return data;
  };

  const {
    data: messages,
    isFetching: loading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<ISolicitation[]>({
    queryKey: ["messages", authUser, selectedCompany, selected],
    queryFn: fetchMessages,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // Se a última página veio com menos de 10 itens, não há mais páginas
      if (!lastPage || lastPage.length < 10) {
        return undefined;
      }
      // Retorna o próximo offset baseado no número de páginas já carregadas
      const nextPage = allPages.length;
      return nextPage;
    },
    refetchOnWindowFocus: true,
    staleTime: 0,
    enabled: !!selectedCompany,
  });

  // Função para carregar mais itens quando o usuário chegar ao final da lista
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Efeito para configurar o IntersectionObserver para carregar mais itens
  useEffect(() => {
    const sentinel = document.getElementById("sentinel");
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.unobserve(sentinel);
    };
  }, [loadMore, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (!authUser || !selectedCompany) {
      history.push("/companies");
      return;
    }

  }, [authUser, selectedCompany, selected]);

  if (!authUser || !selectedCompany) {
    return null;
  }

  const handleMenuAction = (key: string) => {
    if (key === "logout") {
      logout();
      history.push("/login");
    }
  };

  const getScreenTitle = () => {
    return selected === "inbox" ? "Recebidos" : "Enviadas";
  };

  // const handleNavigation = (key: string) => {
  //   setSelected(key);
  // };

  function formatShortDate(dateStr: string) {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
    }
    return dateStr;
  }
  // Recupera mensagens offline do localStorage apenas se estiver na aba de enviados
  const cachedRaw =
    selected === "sent" ? localStorage.getItem("offline-messages") : null;
  const cachedMessages = cachedRaw && !isOnline ? JSON.parse(cachedRaw) : [];

  // Filtra mensagens offline da empresa selecionada
  const filteredOfflineMessages = cachedMessages
    .filter((msg: any) => msg.empresa_id === selectedCompany?.id)
    .map((msg: any) => ({
      id: Math.random() * -1000000, // ID negativo para identificar como offline
      empresaId: Number(msg.empresa_id),
      origem: msg.origem,
      destino: msg.destino,
      assunto: msg.assunto || "Sem assunto",
      mensagem: msg.mensagem,
      status: "offline", // Status especial para mensagens offline
      created_at: new Date().toISOString(),
      count: 1,
      "GROUP_CONCAT(solicitacao.id)": String(Math.random() * -1000000),
      isOffline: true,
    }));

  const hasAnyAttachments = (msg: any) => {
    // Verifica primeiro pelos campos mais comuns
    if (msg.temAnexo > 0 || msg.hasAttachments) return true;

    // Verifica todos os possíveis locais onde anexos podem estar
    const attachmentSources = [
      msg.anexos,
      msg.files,
      msg.arquivos,
      msg.attachments,
      msg.metadata?.anexos,
      msg.metadata?.files,
      msg.documentos,
      msg.imagens
    ];

    // Verifica cada fonte potencial de anexos
    return attachmentSources.some(source => {
      if (!source) return false;

      // Se for array, verifica se tem itens
      if (Array.isArray(source)) return source.length > 0;

      // Se for objeto, verifica se tem propriedades
      if (typeof source === 'object') return Object.keys(source).length > 0;

      // Se for número, verifica se é maior que 0
      if (typeof source === 'number') return source > 0;

      return false;
    });
  };

  // Combina mensagens offline com mensagens do servidor
  const formattedData =
    selected === "sent" && filteredOfflineMessages.length > 0
      ? [
        ...filteredOfflineMessages,
        ...(messages?.pages.flatMap((page) => page || []) || []),
      ]
      : messages?.pages.flatMap((page) => page || []) || [];

  function decodeHtmlEntities(text: string) {
    if (!text) return "";
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-primary-500 pt-4 pb-4 px-4">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
            <div className="rounded-full w-12 h-12 border border-white flex items-center justify-center overflow-hidden flex-shrink-0">
              <img
                src={getLogoBase64()}
                alt="Logo"
                className="max-w-full max-h-full object-contain"
              />
            </div>
            <div className="flex flex-col ml-2 min-w-0">
              <div className="truncate">
                <span className="text-secondary text-base font-bold">
                  {authUser?.bpo?.empresa ?? "Responsável não informado"}
                </span>
              </div>

              <div
                className="flex flex-row items-center gap-1 cursor-pointer min-w-0"
                onClick={onOpen}
              >
                <span className="text-secondary text-sm font-semibold truncate">
                  {selectedCompany.nome}
                </span>
                <Icon
                  icon="lucide:chevron-down"
                  className="text-secondary text-sm flex-shrink-0"
                />
              </div>
            </div>
          </div>
          <Dropdown>
            <DropdownTrigger>
              <Button isIconOnly variant="light">
                <Icon icon="lucide:more-vertical" className="text-secondary" />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Actions"
              onAction={(key) => handleMenuAction(key as string)}
            >
              {/* <DropdownItem
                key="profile"
                startContent={<Icon icon="lucide:user" />}
              >
                Perfil
              </DropdownItem> */}
              <DropdownItem
                key="logout"
                className="text-danger"
                color="danger"
                startContent={<Icon icon="lucide:log-out" />}
              >
                Sair
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        <TabsHeader selected={selected} onSelect={setSelected} />
      </div>

      <div className="bg-white rounded-t-3xl flex-1 min-h-[85vh] max-h-[85vh]">
        <div className="max-w-2xl mx-auto">
          <div className="px-4 pt-6 pb-3 flex items-center justify-between">
            <h2 className="text-xl font-semibold">{getScreenTitle()}</h2>
            <span className="text-xs text-default-400 bg-default-100 px-2 py-1 rounded-full">
              {formattedData?.length} mensagens
            </span>
          </div>
          <div
            className="max-w-2xl mx-auto px-4 py-4   overflow-y-auto"
            style={{ height: "calc(100vh - 280px)" }}
          >
            {loading ? (
              <div className="flex justify-center items-center h-32">
                <Icon
                  icon="lucide:loader"
                  className="animate-spin text-2xl text-primary-500"
                />
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {formattedData?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <Icon
                      icon="lucide:inbox"
                      className="text-gray-300 text-4xl mb-2"
                    />
                    <p className="text-gray-500">Nenhuma mensagem encontrada</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Você ainda não tem mensagens{" "}
                      {selected === "inbox" ? "recebidas" : "enviadas"}
                    </p>
                  </div>
                ) : (
                  formattedData?.map((msg) => (
                    <div
                      key={msg?.id}
                      className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer transition border border-default-200 shadow-sm hover:bg-gray-100 ${msg.status === "pendente"
                        ? "bg-warning-50 border-warning-200"
                        : "bg-white"
                        }`}
                      onClick={() => {
                        if ((msg as any).isOffline) return;

                        // if (selected === 'inbox') {
                        //   const needResponse = msg.resposta != "0";
                        //   if (!needResponse) {
                        //     setSelectedMessage({ id: msg.pai_id || msg.id, companyId: Number(selectedCompany.id) });
                        //     onReadConfirmOpen();
                        //     return;
                        //   }
                        // }

                        history.push({
                          pathname: "/message-details",
                          state: {
                            id: msg.pai_id || msg.id,
                            messages:
                              `${msg["GROUP_CONCAT(solicitacao.id)"]}`?.split(
                                ","
                              ),
                            empresaId: Number(selectedCompany.id),
                            companyName: selectedCompany.nome,
                            selected: selected,
                          },
                        });
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-base truncate">
                            {msg.assunto}
                          </span>
                          {msg.status === "pendente" && (
                            <span className="text-xs bg-warning-200 text-warning-800 px-2 py-0.5 rounded-full ml-1">
                              Pendente
                            </span>
                          )}
                        </div>
                        <div
                          className="text-sm text-gray-700 mb-1 overflow-hidden"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(decodeHtmlEntities(msg.mensagem)),
                          }}
                        ></div>
                        <div className="flex items-center gap-2 text-xs text-gray-400 w-full">
                          {/* Conteúdo principal */}
                          <div className="flex-1 flex items-center gap-2 overflow-hidden">
                            {hasAnyAttachments(msg) && (
                              <div className="flex items-center gap-1">
                                <CgAttachment className="text-gray-400 w-4 h-4" />
                                {(msg.anexos?.length > 1 || msg.files?.length > 1) && (
                                  <span>{msg.anexos?.length || msg.files?.length}</span>
                                )}
                              </div>
                            )}

                            <div className="flex items-center gap-1">
                              <Icon icon="lucide:message-square" className="text-gray-400 w-4 h-4" />
                              <span>{Number(msg.quantidade || 0)}</span>
                            </div>

                            <span className="truncate">{formatShortDate(msg.created_at)}</span>
                          </div>

                          {/* Botão flutuante à direita */}
                          {/* {selected === "inbox" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // evita abrir detalhes ao clicar no botão

                                const needResponse = msg.resposta != "0";
                                if (!needResponse) {
                                  setSelectedMessage({
                                    id: msg.pai_id || msg.id,
                                    companyId: Number(selectedCompany.id),
                                  });
                                  onReadConfirmOpen();
                                  return;
                                }
                              }}
                              className="float-right px-3 py-1 text-xs font-medium rounded-full transition-all duration-200
      bg-gray-100 text-gray-600 hover:bg-gray-200
      border border-gray-300 flex items-center gap-1 ml-2"
                            >
                              <Icon icon="lucide:check-circle" className="w-3.5 h-3.5" />
                              <span>Confirmar leitura</span>
                            </button>
                          )} */}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {(msg as any).isOffline ? (
                          <div className="relative">
                            <Icon
                              icon="lucide:loader"
                              className="text-2xl text-gray-500 animate-spin"
                            />
                          </div>
                        ) : (
                          <BiSolidShare
                            className={`text-2xl ${msg.status === "pendente"
                              ? "text-warning-500"
                              : "text-primary"
                              }`}
                          />
                        )}
                      </div>
                    </div>
                  ))
                )}
                {formattedData && formattedData?.length > 0 && (
                  <div
                    id="sentinel"
                    className="h-10 flex items-center justify-center"
                  >
                    {isFetchingNextPage ? (
                      <Icon
                        icon="lucide:loader"
                        className="animate-spin text-2xl text-primary-500"
                      />
                    ) : null}
                    {!hasNextPage ? (
                      <span className="text-sm text-gray-500">
                        Você chegou ao final da lista
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">
                        Carregar mais mensagens...
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <BottomNavigation selected={selected} />

      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="bottom"
        classNames={{
          base: "max-w-md mx-auto",
          body: "p-0",
        }}
      >
        <ModalContent>
          <ModalBody className="p-0">
            <div className="flex flex-col max-h-[60vh]">
              <div className="flex-1 overflow-y-auto">
                {authUser.contato.empresaLista.map((company) => (
                  <Button
                    key={company.id}
                    className="py-4 px-6 text-gray-700 font-medium border-b border-gray-200 rounded-none hover:bg-gray-50 w-full"
                    variant="light"
                    onPress={() => {
                      setSelectedCompany(company);
                      onOpenChange();
                    }}
                  >
                    {company.nome}
                  </Button>
                ))}
              </div>
              <Button
                className="py-4 px-6 text-gray-400 font-medium rounded-b-lg hover:bg-gray-50 border-t-2 border-gray-200"
                variant="light"
                onPress={onOpenChange}
              >
                Cancelar
              </Button>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isReadConfirmOpen}
        onOpenChange={onReadConfirmChange}
        placement="center"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            Confirmar leitura
          </ModalHeader>
          <ModalBody>
            <p>Deseja marcar a comunicação como lida?</p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              variant="light"
              onPress={() => {
                onReadConfirmChange();
                if (selectedMessage) {
                  history.push({
                    pathname: "/message-details",
                    state: {
                      id: selectedMessage.id,
                      empresaId: selectedMessage.companyId,
                      companyName: selectedCompany.nome,
                      selected: selected,
                    },
                  });
                }
              }}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              onPress={async () => {
                if (selectedMessage) {
                  try {
                    await setNotificationAsAttended(selectedMessage.companyId, selectedMessage.id);
                    history.push({
                      pathname: "/message-details",
                      state: {
                        id: selectedMessage.id,
                        empresaId: selectedMessage.companyId,
                        companyName: selectedCompany.nome,
                        selected: selected,
                      },
                    });
                  } catch (error) {
                    console.error("Error marking message as read:", error);
                  }
                }
                onReadConfirmChange();
              }}
            >
              Confirmar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );

}
